/**
 * 심볼 데이터 관리 Composable
 * 심볼 목록, 필터링, 검색, 관심 종목 관리 등을 담당
 *
 * 설계 문서: docs/symbol-list-data-design.md
 */

import { groupCodeToCategory, type SymbolListTabType, SYMBOL_LIST_TAB } from '@template/types';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import { stockService, watchlistService, quoteService } from '@/services/api';
import type { WatchListAddRequest, OrderStockRequest } from '@template/api';
import { SessionStorageService, SessionStorageKey } from '@template/utils';
import { useSelectedSymbolStore } from '@/stores/useSelectedSymbolStore';
import { useMarketDataStore, useAccountStore } from '@template/stores';
import { calculateChangeRate } from '@template/utils';
import type { TradingSymbol } from '@template/types';
import { useMetadataStore } from '@template/stores';
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import router from '@/router';

/**
 * 심볼 표시 데이터
 * API에서 받아온 데이터를 화면 표시용으로 가공한 타입
 */
export interface SymbolDisplayRow {
  stockCd: string; // 종목코드
  currentPrice: number; // 현재가
  closePrice: number; // 전일종가
  changeRate: number; // 등락률 (%)
  isFavorite: boolean; // 관심종목 여부
}

let orderStocksInitPromise: Promise<void> | null = null;

const createSymbolData = () => {
  const selectedSymbol = useSelectedSymbolStore();
  // 선택된 심볼의 시장 데이터 사용
  const { selectedSymbol: currentSelectedSymbol } = selectedSymbol;

  // Pinia Store 인스턴스 직접 사용 (단일 데이터 소스)
  const marketDataStore = useMarketDataStore();
  const metadataStore = useMetadataStore();

  // 로그인 필요 다이얼로그
  const dialogManager = useGlobalDialogManager();
  const ensureLoggedIn = dialogManager.ensureLoggedIn;

  const { marketData, availableSymbolCodes } = storeToRefs(marketDataStore);
  const { symbolMetadata } = storeToRefs(metadataStore);

  // === 상태 관리 ===

  // 탭별 종목 코드 목록
  // 실제 사용 가능한 종목 목록
  const allSymbols = availableSymbolCodes;
  const watchlistSymbols = ref<string[]>([]);
  const possessionSymbols = ref<string[]>([]);

  // 휘발성 현재가 데이터 (화면 표시용)
  const currentPrices = ref<Map<string, number>>(new Map());

  // UI 상태
  const activeTab = ref<SymbolListTabType>(SYMBOL_LIST_TAB.ALL);

  /**
   * 검색 키워드 초기화 (sessionStorage에서 복원)
   * 화면 간 이동 시 검색어를 유지합니다. (세션 동안 유지)
   */
  const initializeSearchQuery = (): string => {
    try {
      // sessionStorage에서 검색어 복원
      const saved = SessionStorageService.getItem(SessionStorageKey.SYMBOL_LIST_SEARCH_QUERY);
      return typeof saved === 'string' ? saved : '';
    } catch (error) {
      console.error('[useSymbolData] sessionStorage에서 검색어 로드 실패:', error);
      return '';
    }
  };

  const searchQuery = ref(initializeSearchQuery());
  const loading = ref(false);
  const error = ref<string | null>(null);

  // 데이터 소스 설정
  // API 사용
  const symbols = ref<TradingSymbol[]>([]);
  const favorites = ref<Set<string>>(new Set());

  const ensureOrderStocksInitialized = async () => {
    if (metadataStore.isInitialized && marketDataStore.isInitialized) {
      return;
    }

    if (!orderStocksInitPromise) {
      orderStocksInitPromise = (async () => {
        const accountStore = useAccountStore();
        const accountNo = accountStore.selectedAccountNoSafe;

        const request: OrderStockRequest = {
          stockGroupCd: 'ALL',
          stockCd: '',
          accountNo,
        };

        const response = await stockService.getOrderStocks(request);
        const data = response.data;

        if (!data) {
          throw new Error('API 응답 데이터가 없습니다.');
        }

        // 메타데이터 초기화
        metadataStore.initializeMetadata(data.details);

        // 마켓 데이터 초기화
        marketDataStore.initializeMarketDataFromSnapshot(
          data.details,
          data.buyPrices,
          data.sellPrices
        );
      })().finally(() => {
        orderStocksInitPromise = null;
      });
    }

    await orderStocksInitPromise;
  };

  // === API 호출 함수들 ===

  /**
   * 전체 탭: API 응답 기반으로 종목 목록 및 데이터 로드
   * API에서 받은 종목만 포함하도록 초기화
   * MarketDataStore 초기화
   */
  const loadAllSymbols = async () => {
    loading.value = true;
    error.value = null;

    try {
      await ensureOrderStocksInitialized();
    } catch (err: any) {
      error.value = err.message;
      console.error('[useSymbolData] 전체 종목 로드 실패:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * 관심 탭: API 응답 기반으로 관심종목 목록 로드
   * API에서 허용된 종목만 처리
   */
  const loadWatchlistSymbols = async () => {
    loading.value = true;
    error.value = null;

    try {
      await ensureOrderStocksInitialized();

      // 1. 관심종목 코드 목록 조회
      const watchlistResponse = await watchlistService.getWatchList();
      const watchlistData = watchlistResponse.data;

      if (!watchlistData) {
        // console.log('[useSymbolData] 관심종목 데이터가 없습니다.');
        loading.value = false;
        return;
      }

      watchlistSymbols.value = watchlistData.map((item: any) => item.stockCode);

      if (watchlistSymbols.value.length === 0) {
        loading.value = false;
        return;
      }
    } catch (err: any) {
      error.value = err.message;
      console.error('[useSymbolData] 관심종목 로드 실패:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * 보유 탭: API 응답 기반으로 보유종목 목록 로드
   * API에서 허용된 종목만 처리
   */
  const loadPossessionSymbols = async () => {
    loading.value = true;
    error.value = null;

    try {
      await ensureOrderStocksInitialized();

      const accountStore = useAccountStore();
      const accountNo = accountStore.selectedAccountNoSafe;

      // 1. 보유종목 코드 목록 조회
      const possessionRequest = {
        accountNo,
        nextKey: '',
      };
      const possessionResponse = await stockService.getPossessionStocks(possessionRequest);
      const possessionData = possessionResponse.data;

      if (!possessionData) {
        console.error('[useSymbolData] 보유종목 데이터가 없습니다.');
        loading.value = false;
        return;
      }
      possessionSymbols.value = possessionData.stocks || [];

      if (possessionSymbols.value.length === 0) {
        loading.value = false;
        return;
      }
    } catch (err: any) {
      error.value = err.message;
      console.error('[useSymbolData] 보유종목 로드 실패:', err);

      // 실패 시 빈 배열
      symbols.value = [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * 탭별 데이터 로드
   */
  const loadSymbolsByTab = async (tab: SymbolListTabType) => {
    switch (tab) {
      case SYMBOL_LIST_TAB.ALL:
        await loadAllSymbols();
        break;
      case SYMBOL_LIST_TAB.WATCHLIST:
        await loadWatchlistSymbols();
        break;
      case SYMBOL_LIST_TAB.POSSESSION:
        await loadPossessionSymbols();
        break;
      default:
        console.warn(`[useSymbolData] Unknown tab: ${tab}`);
    }
  };

  /**
   * 관심종목 등록/해제 (낙관적 업데이트)
   */
  const toggleFavorite = async (stockCd: string) => {
    // 로그인 체크
    if (!ensureLoggedIn()) {
      return;
    }

    const wasFavorite = watchlistSymbols.value.includes(stockCd);

    // 1. 즉시 UI 업데이트 (낙관적 업데이트)
    if (wasFavorite) {
      watchlistSymbols.value = watchlistSymbols.value.filter((code) => code !== stockCd);
      favorites.value.delete(stockCd); // 임시: Mock용
    } else {
      watchlistSymbols.value.push(stockCd);
      favorites.value.add(stockCd); // 임시: Mock용
    }

    // 2. API 호출
    try {
      if (wasFavorite) {
        // 관심종목 해제
        await watchlistService.removeFromWatchList(stockCd);
        // console.log(`[useSymbolData] 관심종목 해제: ${stockCd}`);
      } else {
        // 관심종목 등록
        const request: WatchListAddRequest = {
          stockCode: stockCd,
        };
        await watchlistService.addToWatchList(request);
        // console.log(`[useSymbolData] 관심종목 등록: ${stockCd}`);
      }
    } catch (err: any) {
      // 3. 실패 시 롤백
      if (wasFavorite) {
        watchlistSymbols.value.push(stockCd);
        favorites.value.add(stockCd); // 임시: Mock용
      } else {
        watchlistSymbols.value = watchlistSymbols.value.filter((code) => code !== stockCd);
        favorites.value.delete(stockCd); // 임시: Mock용
      }

      error.value = '관심종목 등록/해제 실패';
      console.error('[useSymbolData] 관심종목 토글 실패:', err);

      // TODO: 에러 토스트 표시
      // 예시:
      // import { useToast } from '@/composables/useToast';
      // const { showError } = useToast();
      // showError('관심종목 등록/해제에 실패했습니다.');
    }
  };

  /**
   * 종목이 관심종목인지 확인
   */
  const isFavorite = (stockCd: string): boolean => {
    return watchlistSymbols.value.includes(stockCd) || favorites.value.has(stockCd);
  };

  /**
   * 특정 종목의 데이터 조회
   * @param stockCd - 종목 코드
   * @returns 종목의 표시용 데이터 또는 null (종목이 없는 경우)
   */
  const getSymbolByCode = (stockCd: string): SymbolDisplayRow | null => {
    const metadata = metadataStore.getMetadata(stockCd);

    if (!metadata) {
      return null;
    }

    // marketData에서 실시간 데이터 가져오기
    const symbolData = marketData.value.find((data) => data.symbol === stockCd);
    const currentPrice = symbolData?.sellPrice ?? 0;
    const closePrice = metadata.closePrice ?? 0;

    // 등락률 계산
    const changeRate = calculateChangeRate(currentPrice, closePrice);

    return {
      stockCd,
      currentPrice,
      closePrice,
      changeRate,
      isFavorite: isFavorite(stockCd),
    };
  };

  /**
   * 종목 선택
   */
  const selectSymbol = (symbol: TradingSymbol) => {
    selectedSymbol.setSelectedSymbol(symbol.ticker);

    return symbol;
  };

  /**
   * 종목 그룹 코드를 타입으로 변환
   */
  const getSymbolType = (stockGroupCd?: string): string => {
    switch (stockGroupCd) {
      case '01':
        return 'forex';
      case '02':
        return 'index';
      case '03':
        return 'commodity';
      case '04':
        return 'crypto';
      default:
        return 'forex';
    }
  };

  /**
   * SymbolDisplayRow를 TradingSymbol로 변환
   */
  const convertToTradingSymbol = (symbol: SymbolDisplayRow): TradingSymbol => {
    const metadata = symbolMetadata.value.get(symbol.stockCd);
    return {
      symbol: symbol.stockCd,
      ticker: symbol.stockCd,
      type: getSymbolType(metadata?.stockGroupCd),
      // TODO: description, exchange는 현재 API에는 없음. 필요여부 확인
    };
  };

  /**
   * 현재 활성 탭에 따라 적절한 데이터 로드
   */
  const loadSymbols = async () => {
    await loadSymbolsByTab(activeTab.value);
  };

  // === 계산된 속성 ===

  /**
   * 현재 탭의 표시용 데이터 목록
   * 종목 코드는 즉시 표시하고, 메타데이터/현재가는 로드되는 대로 업데이트
   * searchQuery에 따라 필터링됨
   */
  const displaySymbols = computed<SymbolDisplayRow[]>(() => {
    const currentSymbolCodes =
      activeTab.value === SYMBOL_LIST_TAB.ALL
        ? allSymbols.value
        : activeTab.value === SYMBOL_LIST_TAB.WATCHLIST
          ? watchlistSymbols.value
          : possessionSymbols.value;

    // 표시용 데이터 생성 - useMarketData의 데이터 직접 사용
    const allDisplayData = currentSymbolCodes.map((stockCd) => {
      const metadata = metadataStore.getMetadata(stockCd);

      // marketData computed에서 실시간 데이터 가져오기
      const symbolData = marketData.value.find((data) => data.symbol === stockCd);
      const currentPrice = symbolData?.sellPrice ?? 0;
      const closePrice = metadata?.closePrice ?? 0;

      // 등락률 계산
      const changeRate = calculateChangeRate(currentPrice, closePrice);

      return {
        stockCd,
        currentPrice: currentPrice,
        closePrice,
        changeRate,
        isFavorite: isFavorite(stockCd),
      };
    });

    // 검색어가 있으면 필터링
    if (searchQuery.value.trim() === '') {
      return allDisplayData;
    }

    const query = searchQuery.value.toLowerCase().trim();
    return allDisplayData.filter((symbol) => {
      // 종목 코드로 검색
      return symbol.stockCd.toLowerCase().includes(query);
    });
  });

  /**
   * 카테고리별 필터링된 데이터
   * 메타데이터의 stockGroupCd 기준으로 그룹핑 (서버 응답 종목 전부 표시)
   * SYMBOL_CATEGORIES는 초기값용이며, 실제 표시는 서버 메타데이터 기준
   */
  const displaySymbolsByCategory = computed(() => {
    const all = displaySymbols.value;

    const forex: SymbolDisplayRow[] = [];
    const index: SymbolDisplayRow[] = [];
    const commodity: SymbolDisplayRow[] = [];
    const crypto: SymbolDisplayRow[] = [];

    for (const s of all) {
      const metadata = metadataStore.getMetadata(s.stockCd);
      const category = groupCodeToCategory(metadata?.stockGroupCd);

      switch (category) {
        case 'forex':
          forex.push(s);
          break;
        case 'index':
          index.push(s);
          break;
        case 'commodity':
          commodity.push(s);
          break;
        case 'crypto':
          crypto.push(s);
          break;
        default:
          // stockGroupCd 미지정 시 외환으로 분류
          forex.push(s);
      }
    }

    return { all, forex, index, commodity, crypto };
  });

  /**
   * 카테고리별 종목 존재 여부 (렌더링 최적화용)
   * 표시되는 종목 기준으로 판단
   */
  const hasCategorySymbols = computed(() => ({
    forex: displaySymbolsByCategory.value.forex.length > 0,
    index: displaySymbolsByCategory.value.index.length > 0,
    commodity: displaySymbolsByCategory.value.commodity.length > 0,
    crypto: displaySymbolsByCategory.value.crypto.length > 0,
  }));

  /**
   * 검색 키워드 변경 감지하여 sessionStorage에 저장
   * 화면 간 이동 시 검색어를 유지하기 위함
   */
  watch(
    searchQuery,
    (newQuery) => {
      try {
        if (newQuery.trim() === '') {
          // 빈 검색어는 sessionStorage에서 제거
          SessionStorageService.removeItem(SessionStorageKey.SYMBOL_LIST_SEARCH_QUERY);
        } else {
          // 검색어가 있으면 sessionStorage에 저장
          SessionStorageService.setItem(SessionStorageKey.SYMBOL_LIST_SEARCH_QUERY, newQuery);
        }
      } catch (error) {
        console.error('[useSymbolData] sessionStorage에 검색어 저장 실패:', error);
      }
    },
    { immediate: false }
  );

  // === 계좌 변경 감지 ===
  // 계좌 변경 시 보유 종목은 계좌별 데이터이므로 업데이트
  const accountStore = useAccountStore();
  watch(
    () => accountStore.selectedAccountNo,
    async (newAccount) => {
      if (!newAccount) {
        possessionSymbols.value = [];
        return;
      }
      await loadPossessionSymbols();
    }
  );

  // === 계좌 등급 변경 감지 ===
  // 계좌 등급 변경 시 전체 종목 조회 API 재호출
  // 계좌 변경 시 watch 패턴과 유사하게 CustomEvent로 처리
  // 주의: 이 이벤트 리스너는 전역적으로 등록되므로 cleanup이 필요할 수 있으나,
  // useSymbolData는 싱글톤 패턴이므로 컴포넌트 언마운트 시에도 유지됨
  let accountGradeChangedListener: ((event: CustomEvent) => Promise<void>) | null = null;

  const setupAccountGradeChangedListener = () => {
    if (accountGradeChangedListener || typeof window === 'undefined') {
      return;
    }

    accountGradeChangedListener = async (event: CustomEvent) => {
      const { accountNo } = event.detail;
      const accountStore = useAccountStore();

      // 현재 선택된 계좌와 일치하는 경우에만 처리
      // 주의: 이벤트 발생 시점과 계좌 선택 시점의 타이밍 이슈로 인해
      // accountNo가 일치하지 않을 수 있으므로, 현재 선택된 계좌를 기준으로 처리
      const currentAccountNo = accountStore.selectedAccountNo;
      if (!currentAccountNo) {
        return;
      }

      // 이벤트의 accountNo와 현재 선택된 계좌가 일치하는지 확인
      if (accountNo !== currentAccountNo) {
        return;
      }

      // orderStocksInitPromise를 null로 초기화하여 재호출 가능하도록 함
      orderStocksInitPromise = null;

      // MarketDataStore와 MetadataStore의 isInitialized 상태 확인 및 강제 초기화
      // isInitialized를 강제로 false로 설정하여 재초기화 보장
      // @ts-ignore - private 필드 접근
      metadataStore.isInitialized = false;
      // @ts-ignore - private 필드 접근
      marketDataStore.isInitialized = false;

      try {
        // ensureOrderStocksInitialized() 호출 시 자동으로 재초기화
        await ensureOrderStocksInitialized();

        // 전체 종목 데이터 다시 로드
        await loadAllSymbols();

        // 선택된 종목의 OHLC 데이터 재조회
        const { useSelectedSymbolStore } = await import('@/stores/useSelectedSymbolStore');
        const selectedSymbolStore = useSelectedSymbolStore();
        const selectedSymbol = selectedSymbolStore.selectedSymbol;
        if (selectedSymbol) {
          const accountStore = useAccountStore();
          const accountNo = accountStore.selectedAccountNoSafe;
          await marketDataStore.loadDailyHighLow(selectedSymbol, quoteService, accountNo);
        }
      } catch (error) {
        console.error('[useSymbolData] 계좌 등급 변경 시 종목 재조회 실패:', error);
      }
    };

    window.addEventListener(
      'accountGradeChanged',
      accountGradeChangedListener as unknown as EventListener
    );
  };

  // 이벤트 리스너 등록 (createSymbolData 함수가 호출될 때 즉시 등록)
  setupAccountGradeChangedListener();

  // === 반환 ===

  return {
    // 상태
    symbols, // 임시: Mock용
    favorites, // 임시: Mock용
    allSymbols,
    watchlistSymbols,
    possessionSymbols,
    currentPrices,
    activeTab,
    searchQuery,
    currentSelectedSymbol,
    marketData,
    loading,
    error,

    // 계산된 속성
    displaySymbols, // API 연동용
    displaySymbolsByCategory, // 카테고리별 필터링된 데이터
    hasCategorySymbols, // 카테고리별 종목 존재 여부

    // 메서드
    selectSymbol,
    toggleFavorite,
    isFavorite,
    getSymbolByCode,
    convertToTradingSymbol,
    loadSymbols, // 임시: Mock용
    loadAllSymbols,
    loadWatchlistSymbols,
    loadPossessionSymbols,
    loadSymbolsByTab,
  };
};

type SymbolDataComposable = ReturnType<typeof createSymbolData>;

let symbolDataInstance: SymbolDataComposable | null = null;

/**
 * 심볼 데이터 관리 Composable
 */
export function useSymbolData(): SymbolDataComposable {
  if (!symbolDataInstance) {
    symbolDataInstance = createSymbolData();
  }

  return symbolDataInstance;
}
